import { useState, useEffect } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";
import { Loader2 } from "lucide-react";
import PageShell from "@/components/PageShell";
import ModularPage from "@/pages/ModularPage";
import CustomPageBody from "@/components/CustomPageBody";

const API = getApiUrl();

/**
 * CmsPageOverride - wraps a hardcoded page route (e.g. /about, /prices).
 * If an admin has built a CMS Modular Page or Custom Page at this exact
 * slug, that is served instead of the hardcoded `fallback` component.
 * Priority: Modular Page > Custom Page > hardcoded fallback.
 */
export default function CmsPageOverride({ slug, fallback: Fallback }) {
  const [mode, setMode] = useState("checking"); // checking | modular | custom | fallback
  const [customPage, setCustomPage] = useState(null);

  useEffect(() => {
    let mounted = true;
    setMode("checking");
    setCustomPage(null);
    // Not routed through the shared pageCompositionCache that <ModularPage>
    // uses — nesting that cache's pre-check in a wrapper like this caused
    // an intermittent stuck render in testing; <ModularPage>'s own fetch
    // (rendered below once we know it's modular) still benefits from it.
    Promise.allSettled([
      axios.get(`${API}/page-composition/${slug}`),
      axios.get(`${API}/custom-static-page/${slug}`),
    ]).then(([modularResult, customResult]) => {
      if (!mounted) return;
      if (modularResult.status === "fulfilled") {
        setMode("modular");
      } else if (customResult.status === "fulfilled") {
        setCustomPage(customResult.value.data);
        setMode("custom");
      } else if (
        modularResult.reason?.response?.status === 404 &&
        customResult.reason?.response?.status === 404
      ) {
        // Neither a Modular nor a Custom page exists for this slug.
        setMode("fallback");
      } else {
        // A transient error (network/5xx) — don't silently hide the admin's
        // CMS page behind the hardcoded fallback; just show the fallback for
        // now since we can't tell which page they meant to serve.
        setMode("fallback");
      }
    });
    return () => {
      mounted = false;
    };
  }, [slug]);

  if (mode === "checking") {
    return (
      <PageShell>
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </PageShell>
    );
  }

  if (mode === "modular") return <ModularPage fixedSlug={slug} />;
  if (mode === "custom") return <CustomPageBody page={customPage} />;
  return <Fallback />;
}
